import React, { useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, ButtonVariant } from '../../../common/Button'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { FormControl } from '../../../common/FormControl'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { isUserAdminInSociety } from '../../../../helpers/society'
import { houseRuleCreateSchema } from '../../../../forms/schemas/house_rule_create'
import { TextArea } from '../../../common/TextArea'
import { TextInput } from '../../../common/TextInput'
import { NOTIFICATION_SETTINGS_VALUE } from '../../../../helpers/notification_settings_options'
import { IHouseRuleModel } from '../../../../interfaces/models/house-rules.interfaces'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import { useAuth } from '../../../../hooks/useAuth'

interface CreateUpdateHouseRuleFormProps {
  onError: (error: unknown) => void
  onSubmit: (data: InferType<typeof houseRuleCreateSchema>) => Promise<void>
  onClose: () => void
  loading: boolean
  houseRule?: IHouseRuleModel
  society: ISocietiesModel
}

export const CreateUpdateHouseRuleForm = ({
  houseRule,
  loading,
  onError,
  onSubmit,
  onClose,
  society,
}: CreateUpdateHouseRuleFormProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { userId } = useAuth()
  const { setToastNotification } = useToastNotifications()
  const { getErrorMessage } = useFormErrorMessage()

  const updateMode = !!houseRule

  const isAdmin = isUserAdminInSociety(society, userId as string)

  if (!isAdmin || (updateMode && houseRule === undefined)) {
    onClose()
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const getDefaultValues = useCallback((): InferType<
    typeof houseRuleCreateSchema
  > => {
    return {
      rule: updateMode ? houseRule?.rule || '' : '',
      section: updateMode ? houseRule?.section || '' : '',
      society: society._id,
      notificationSettings: updateMode
        ? houseRule?.notificationSettings || NOTIFICATION_SETTINGS_VALUE.NONE
        : NOTIFICATION_SETTINGS_VALUE.NONE,
    }
  }, [society, houseRule, updateMode])

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(houseRuleCreateSchema),
    defaultValues: getDefaultValues(),
  })

  return (
    <>
      <Controller
        control={control}
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            label={translate('createUpdateHouseRuleForm.labels.subject')}
            error={errors.section && getErrorMessage(errors.section)}
            name={name}
          >
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.section}
            />
          </FormControl>
        )}
        name="section"
      />
      <Controller
        control={control}
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            label={translate('createUpdateHouseRuleForm.labels.rule')}
            error={errors.rule && getErrorMessage(errors.rule)}
            name={name}
          >
            <TextArea
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.rule}
              rows={16}
            />
          </FormControl>
        )}
        name="rule"
      />
      <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-3">
        <Button
          label={translate('common.actions.cancel')}
          onClick={onClose}
          variant={ButtonVariant.TEXT}
        />
        <Button
          variant={ButtonVariant.PRIMARY}
          disabled={!isValid || loading}
          label={translate('common.actions.save')}
          type="submit"
          onClick={handleSubmit(onSubmit, onError)}
        />
      </div>
    </>
  )
}
